import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Loader2, RefreshCw, FileText, Send } from "lucide-react";
import XLogo from "@/components/XLogo";
import { API } from "@/data/content";

const inputCls =
  "w-full rounded-lg border border-line bg-panel px-4 py-3 font-mono text-sm text-white placeholder:text-neutral-600 outline-none focus:border-brand";

export default function AdminPage() {
  const [key, setKey] = useState(() => sessionStorage.getItem("teamx-admin-key") || "");
  const [data, setData] = useState(null);
  const [articles, setArticles] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [artForm, setArtForm] = useState({
    title: "",
    excerpt: "",
    category: "Market Research",
    tags: "",
    content: "",
  });
  const [publishing, setPublishing] = useState(false);

  const load = async (k = key) => {
    if (!k) return;
    setLoading(true);
    setError("");
    try {
      const [{ data }, arts] = await Promise.all([
        axios.get(`${API}/admin/submissions`, { headers: { "X-Admin-Key": k } }),
        axios.get(`${API}/articles`),
      ]);
      setData(data);
      setArticles(arts.data.articles || []);
      sessionStorage.setItem("teamx-admin-key", k);
    } catch {
      setError("Invalid admin key or server error.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const publish = async (e) => {
    e.preventDefault();
    setPublishing(true);
    try {
      const { data: res } = await axios.post(`${API}/articles`, artForm, {
        headers: { "X-Admin-Key": key },
      });
      toast.success(`Article published at /insights/${res.slug}`);
      setArtForm({ title: "", excerpt: "", category: "Market Research", tags: "", content: "" });
      const arts = await axios.get(`${API}/articles`);
      setArticles(arts.data.articles || []);
    } catch {
      toast.error("Publishing failed. Check your admin key.");
    } finally {
      setPublishing(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setArt = (k) => (e) => setArtForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="dark min-h-screen bg-void px-4 py-10 text-ink sm:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center gap-3">
          <XLogo size={36} />
          <div>
            <h1 className="font-display text-3xl font-black uppercase tracking-wide text-white">
              TEAM X <span className="text-brand">Admin</span>
            </h1>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
              Submissions Console
            </p>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            load();
          }}
          className="mt-8 flex max-w-md gap-2"
        >
          <input
            data-testid="admin-key-input"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Admin key"
            className="flex-1 rounded-lg border border-line bg-panel px-4 py-3 font-mono text-sm text-white outline-none focus:border-brand"
          />
          <button
            data-testid="admin-load-button"
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 rounded-lg bg-brand px-5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white hover:bg-brand-crimson disabled:opacity-60"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            Load
          </button>
        </form>
        {error && (
          <p data-testid="admin-error" className="mt-3 font-mono text-xs text-brand">
            {error}
          </p>
        )}

        {data && (
          <div data-testid="admin-submissions" className="mt-10 space-y-10">
            <div className="flex items-center justify-between">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-400">
                {data.contacts.length} contacts · {data.newsletter.length} subscribers ·{" "}
                {data.bookings?.length || 0} bookings · {articles.length} articles ·{" "}
                {data.chat_message_count} chat messages
              </p>
              <button
                data-testid="admin-refresh-button"
                onClick={() => load()}
                className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-neutral-400 hover:text-brand"
              >
                <RefreshCw size={12} /> Refresh
              </button>
            </div>

            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-wide text-white">
                Consultation Bookings
              </h2>
              <div className="mt-4 overflow-x-auto rounded-xl border border-line">
                <table className="w-full min-w-[720px] text-left font-mono text-xs">
                  <thead className="bg-panel text-neutral-400">
                    <tr>
                      {["Date", "Slot (UTC)", "Name", "Email", "Phone", "Service", "Status"].map((h) => (
                        <th key={h} className="px-4 py-3 font-bold uppercase tracking-[0.12em]">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line text-neutral-300">
                    {(data.bookings || []).map((b) => (
                      <tr key={b.id} className="bg-surface">
                        <td className="whitespace-nowrap px-4 py-3 font-bold text-white">{b.date}</td>
                        <td className="px-4 py-3 text-brand">{b.slot}</td>
                        <td className="px-4 py-3 font-bold text-white">{b.name}</td>
                        <td className="px-4 py-3">{b.email}</td>
                        <td className="px-4 py-3">{b.phone || "—"}</td>
                        <td className="px-4 py-3">{b.service}</td>
                        <td className="px-4 py-3">
                          <span className="rounded-full border border-gold/40 bg-gold/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-gold">
                            {b.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {!(data.bookings || []).length && (
                      <tr>
                        <td colSpan={7} className="bg-surface px-4 py-8 text-center text-neutral-500">
                          No bookings yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-wide text-white">
                Consultation Requests
              </h2>
              <div className="mt-4 overflow-x-auto rounded-xl border border-line">
                <table className="w-full min-w-[720px] text-left font-mono text-xs">
                  <thead className="bg-panel text-neutral-400">
                    <tr>
                      {["Date", "Name", "Email", "Phone", "Service", "Message"].map((h) => (
                        <th key={h} className="px-4 py-3 font-bold uppercase tracking-[0.12em]">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line text-neutral-300">
                    {data.contacts.map((c) => (
                      <tr key={c.id} className="bg-surface">
                        <td className="whitespace-nowrap px-4 py-3 text-neutral-500">
                          {new Date(c.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 font-bold text-white">{c.name}</td>
                        <td className="px-4 py-3">{c.email}</td>
                        <td className="px-4 py-3">{c.phone || "—"}</td>
                        <td className="px-4 py-3 text-brand">{c.service}</td>
                        <td className="max-w-xs truncate px-4 py-3">{c.message}</td>
                      </tr>
                    ))}
                    {!data.contacts.length && (
                      <tr>
                        <td colSpan={6} className="bg-surface px-4 py-8 text-center text-neutral-500">
                          No consultation requests yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-wide text-white">
                Newsletter Subscribers
              </h2>
              <div className="mt-4 overflow-x-auto rounded-xl border border-line">
                <table className="w-full min-w-[420px] text-left font-mono text-xs">
                  <thead className="bg-panel text-neutral-400">
                    <tr>
                      <th className="px-4 py-3 font-bold uppercase tracking-[0.12em]">Date</th>
                      <th className="px-4 py-3 font-bold uppercase tracking-[0.12em]">Email</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line text-neutral-300">
                    {data.newsletter.map((s) => (
                      <tr key={s.id} className="bg-surface">
                        <td className="whitespace-nowrap px-4 py-3 text-neutral-500">
                          {new Date(s.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">{s.email}</td>
                      </tr>
                    ))}
                    {!data.newsletter.length && (
                      <tr>
                        <td colSpan={2} className="bg-surface px-4 py-8 text-center text-neutral-500">
                          No subscribers yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="flex items-center gap-2 font-display text-xl font-bold uppercase tracking-wide text-white">
                <FileText size={18} className="text-brand" /> Insight Articles
              </h2>

              <form
                data-testid="admin-article-form"
                onSubmit={publish}
                className="mt-4 space-y-3 rounded-xl border border-line bg-surface p-5"
              >
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-brand">
                  Publish New Article
                </p>
                <input
                  data-testid="admin-article-title-input"
                  required
                  value={artForm.title}
                  onChange={setArt("title")}
                  placeholder="Article title"
                  className={inputCls}
                />
                <textarea
                  data-testid="admin-article-excerpt-input"
                  required
                  value={artForm.excerpt}
                  onChange={setArt("excerpt")}
                  placeholder="Excerpt (1-2 sentences shown in cards and search results)"
                  rows={2}
                  className={`${inputCls} resize-none`}
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  <select
                    data-testid="admin-article-category-select"
                    value={artForm.category}
                    onChange={setArt("category")}
                    className={inputCls}
                  >
                    {["Market Research", "Technology Sector Insights", "Risk Awareness Education", "Global Market Analysis", "Investment Education", "Portfolio Guidance"].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <input
                    data-testid="admin-article-tags-input"
                    value={artForm.tags}
                    onChange={setArt("tags")}
                    placeholder="Tags, comma separated"
                    className={inputCls}
                  />
                </div>
                <textarea
                  data-testid="admin-article-content-input"
                  required
                  value={artForm.content}
                  onChange={setArt("content")}
                  placeholder={"Article body. Separate paragraphs with a blank line. Start a line with '## ' for a section heading."}
                  rows={8}
                  className={`${inputCls} resize-y`}
                />
                <button
                  data-testid="admin-article-publish-button"
                  type="submit"
                  disabled={publishing}
                  className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-white hover:bg-brand-crimson disabled:opacity-60"
                >
                  {publishing ? <Loader2 size={14} className="animate-spin" /> : <Send size={13} />}
                  Publish Article
                </button>
              </form>

              <div className="mt-4 overflow-hidden rounded-xl border border-line">
                {articles.map((a) => (
                  <div
                    key={a.slug}
                    className="flex items-center justify-between gap-4 border-b border-line bg-surface px-4 py-3 last:border-0"
                  >
                    <div>
                      <p className="font-mono text-xs font-bold text-white">{a.title}</p>
                      <p className="font-mono text-[10px] text-neutral-500">
                        /insights/{a.slug} · {a.category}
                      </p>
                    </div>
                    <a
                      data-testid={`admin-article-view-${a.slug}`}
                      href={`/insights/${a.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 font-mono text-[10px] uppercase tracking-[0.15em] text-brand hover:underline"
                    >
                      View
                    </a>
                  </div>
                ))}
                {!articles.length && (
                  <p className="bg-surface px-4 py-8 text-center font-mono text-xs text-neutral-500">
                    No articles yet.
                  </p>
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
